import { getOutPorts } from './useCsmlPorts';

/**
 * Pure CSML generation from visual graph nodes/connections.
 * Ported from builder4.html generateCSML.
 */

export function escapeCSML(str) {
  return String(str ?? '')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n');
}

// POST, PUT and PATCH take the JSON body. GET and DELETE do not.
const HTTP_BODY_METHODS = new Set(['post', 'put', 'patch']);

// A successful .send() returns the JSON body. HTTP status and headers live on
// get_info(); reading response.status looks up a body key and errors.
function httpResponseSubject(field) {
  const name = field || 'status';
  if (name === 'status') return 'response.get_info().status';
  if (name === 'headers' || name === 'header')
    return 'response.get_info().headers';
  return `response.${name}`;
}

function csmlJsonObject(raw) {
  const text = String(raw ?? '').trim();
  if (!text) return { literal: '', invalid: false, authScheme: 'none' };
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { literal: '', invalid: true, authScheme: 'none' };
    }
    return {
      literal: JSON.stringify(parsed),
      invalid: false,
      authScheme: 'none',
    };
  } catch {
    return { literal: '', invalid: true, authScheme: 'none' };
  }
}

// RFC 9110 auth-scheme is a token, then whitespace, then the credential.
const HTTP_AUTH_CREDENTIALS = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+\s+\S/;

// OpenAI and most JSON APIs require an auth-scheme. A raw token in
// Authorization is not a valid header and the API returns 401. A value that
// already names a scheme (Bearer, DPoP, Negotiate, …) is left unchanged.
function withAuthorizationScheme(headers) {
  if (!headers.literal) return headers;
  const parsed = JSON.parse(headers.literal);
  let authScheme = 'none';
  Object.keys(parsed).forEach(key => {
    if (key.toLowerCase() !== 'authorization') return;
    const value = String(parsed[key] ?? '').trim();
    if (!value) return;
    if (HTTP_AUTH_CREDENTIALS.test(value)) {
      authScheme = 'already-set';
      parsed[key] = value;
      return;
    }
    parsed[key] = `Bearer ${value}`;
    authScheme = 'bearer-prefixed';
  });
  return {
    literal: JSON.stringify(parsed),
    invalid: false,
    authScheme,
  };
}

function apiSay(content) {
  return String(content ?? '').replaceAll(
    '{{response.status}}',
    '{{response.get_info().status}}'
  );
}

function connectionsFrom(connections, nodeId) {
  return connections.filter(c => c.fromId === nodeId);
}

function appendQuestion(node, connections) {
  let block = '';
  (node.buttons || []).forEach((button, index) => {
    const payload = button.payload
      ? `, payload="${escapeCSML(button.payload)}"`
      : '';
    block += `  do btn_${node.id}_${index} = Button("${escapeCSML(button.label)}"${payload})\n`;
  });
  const names = (node.buttons || []).map(
    (_, index) => `btn_${node.id}_${index}`
  );
  const buttonType =
    node.buttonType === 'quick_reply' ? ', button_type="quick_reply"' : '';
  block += `  say Question("${escapeCSML(node.content)}"${buttonType}, buttons=[${names.join(',')}])\n`;
  const buttonConnections = connectionsFrom(connections, node.id);
  if (buttonConnections.length > 0) {
    block += `  hold\n`;
    buttonConnections.forEach(connection => {
      const buttonIndex =
        connection.fromPortIdx !== undefined ? connection.fromPortIdx : 0;
      if (node.buttons?.[buttonIndex]) {
        block += `  if (event match btn_${node.id}_${buttonIndex}) { goto node_${connection.toId} }\n`;
      }
    });
  }
  return block;
}

function rememberedEvent(name) {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(String(name || ''));
}

// Invalid headers or a body the method will send must not become a request.
// The call is omitted so preview, export, and save cannot send it without
// the configuration the user typed. The editor also blocks save.
export function nodeHasInvalidApiJson(node) {
  if (node?.type !== 'api') return false;
  if (csmlJsonObject(node.apiHeaders).invalid) return true;
  const method = String(node.apiMethod || 'GET').toLowerCase();
  return HTTP_BODY_METHODS.has(method) && csmlJsonObject(node.apiBody).invalid;
}

export function generateCSML(nodes, connections) {
  const start = nodes.find(n => n.type === 'start');
  if (!start) return '// No start node. Add a Start Node.';

  let csml = 'start:\n';
  if (start.buttons?.length) {
    csml += `${appendQuestion(start, connections)}\n`;
  } else {
    if (String(start.content || '').trim()) {
      csml += `  say "${escapeCSML(start.content)}"\n`;
    }
    const nextFromStart = connectionsFrom(connections, start.id);
    if (nextFromStart.length > 0) {
      csml += `  goto node_${nextFromStart[0].toId}\n\n`;
    } else {
      csml += `\n`;
    }
  }

  nodes.forEach(node => {
    if (node.type === 'start') return;
    csml += `node_${node.id}:\n`;

    if (node.type === 'say') {
      csml += `  say "${escapeCSML(node.content)}"\n`;
    } else if (node.type === 'typing') {
      csml += `  say Typing(${node.duration || 1000})\n`;
    } else if (node.type === 'image') {
      csml += `  say Image("${escapeCSML(node.content)}")\n`;
    } else if (node.type === 'video') {
      csml += `  say Video("${escapeCSML(node.content)}")\n`;
    } else if (node.type === 'audio') {
      csml += `  say Audio("${escapeCSML(node.content)}")\n`;
    } else if (node.type === 'file') {
      csml += `  say File("${escapeCSML(node.content)}")\n`;
    } else if (node.type === 'url') {
      const hide = node.hideUrl ? ', hide_url=true' : '';
      csml += `  say Url("${escapeCSML(node.content)}", text="${escapeCSML(node.urlText || 'Link')}"${hide})\n`;
    } else if (node.type === 'latex') {
      const tts = node.tts ? `, tts="${escapeCSML(node.tts)}"` : '';
      csml += `  say LaTeX("${escapeCSML(node.content)}"${tts})\n`;
    } else if (node.type === 'signature') {
      const desc = node.description
        ? `, description="${escapeCSML(node.description)}"`
        : '';
      const label = node.submit_label
        ? `, submit_label="${escapeCSML(node.submit_label)}"`
        : '';
      csml += `  say Signature("${escapeCSML(node.content)}"${desc}${label})\n`;
    } else if (node.type === 'userinput') {
      csml += `  say UserInput(${node.enabled !== false ? 'true' : 'false'})\n`;
    } else if (node.type === 'router') {
      const routerConnections = connectionsFrom(connections, node.id);
      (node.keywords || []).forEach((rule, index) => {
        const connection = routerConnections.find(
          item => item.fromPortIdx === index + 1
        );
        if (!connection) return;
        csml += `  if (event match "${escapeCSML(rule.match)}") { goto node_${connection.toId} }\n`;
      });
      const fallthrough = routerConnections.find(
        item => (item.fromPortIdx ?? 0) === 0
      );
      if (fallthrough) csml += `  goto node_${fallthrough.toId}\n`;
    } else if (node.type === 'api' && nodeHasInvalidApiJson(node)) {
      csml += `  // API node skipped: headers and body must be JSON objects\n`;
    } else if (node.type === 'api' && node.callStyle === 'http.post') {
      // Production bots call HTTP.post(url, headers, body) and read the
      // parsed body. Palette nodes keep the fluent .send() form below.
      if (rememberedEvent(node.eventMemory)) {
        csml += `  remember ${node.eventMemory} = event\n`;
      }
      const headers = csmlJsonObject(node.apiHeaders);
      const body = csmlJsonObject(node.apiBody);
      csml += `  do response = HTTP.post("${escapeCSML(node.apiUrl)}", ${
        headers.literal || '{}'
      }, ${body.literal || '{}'})\n`;
      const field = rememberedEvent(node.responseField)
        ? node.responseField
        : 'response';
      csml += `  if (response.is_error()) {\n`;
      csml += `    say "${escapeCSML(node.errorMessage || '')}"\n`;
      csml += `  } else {\n`;
      csml += `    do data = json.parse(response.getBody())\n`;
      csml += `    say "{{data.${field}}}"\n`;
      csml += `  }\n`;
      const next = connectionsFrom(connections, node.id).find(
        item => (item.fromPortIdx ?? 0) === 0
      );
      if (next) csml += `  goto node_${next.toId}\n`;
    } else if (node.type === 'api') {
      const method = (node.apiMethod || 'GET').toLowerCase();
      const headers = withAuthorizationScheme(csmlJsonObject(node.apiHeaders));
      const body = csmlJsonObject(node.apiBody);
      const sendsBody = HTTP_BODY_METHODS.has(method);
      const usesUserInput =
        `${node.apiHeaders || ''}\n${node.apiBody || ''}`.includes(
          '{{user_input}}'
        );
      if (usesUserInput) {
        csml += `  do user_input = event\n`;
      }
      let call = `HTTP("${escapeCSML(node.apiUrl)}")`;
      if (headers.literal) call += `.set(${headers.literal})`;
      call +=
        sendsBody && body.literal
          ? `.${method}(${body.literal})`
          : `.${method}()`;
      call += '.send()';
      csml += `  do response = ${call}\n`;
      if (node.content) {
        csml += `  say "${escapeCSML(apiSay(node.content))}"\n`;
      }
      if (node.responseRules && node.responseRules.length > 0) {
        const ruleConns = connectionsFrom(connections, node.id);
        node.responseRules.forEach((rule, idx) => {
          const portIdx = idx + 1;
          const targetConn = ruleConns.find(c => c.fromPortIdx === portIdx);
          if (targetConn) {
            const subject = httpResponseSubject(rule.field);
            let condition = '';
            switch (rule.operator) {
              case 'equals':
                condition = ` == ${
                  Number.isNaN(Number(rule.value))
                    ? `"${escapeCSML(rule.value)}"`
                    : rule.value
                }`;
                break;
              case 'contains':
                condition = `.contains("${escapeCSML(rule.value)}")`;
                break;
              case 'starts':
                condition = `.starts_with("${escapeCSML(rule.value)}")`;
                break;
              case 'ends':
                condition = `.ends_with("${escapeCSML(rule.value)}")`;
                break;
              case 'gt':
                if (
                  String(rule.value ?? '').trim() === '' ||
                  !Number.isFinite(Number(rule.value))
                ) {
                  csml += `  // Rule ${idx + 1} skipped: "${rule.operator}" needs a numeric value\n`;
                  return;
                }
                condition = ` > ${rule.value}`;
                break;
              case 'lt':
                if (
                  String(rule.value ?? '').trim() === '' ||
                  !Number.isFinite(Number(rule.value))
                ) {
                  csml += `  // Rule ${idx + 1} skipped: "${rule.operator}" needs a numeric value\n`;
                  return;
                }
                condition = ` < ${rule.value}`;
                break;
              case 'exists':
                condition = ` != null`;
                break;
              default:
                condition = ` == "${escapeCSML(rule.value)}"`;
            }
            csml += `  if (${subject}${condition}) { goto node_${targetConn.toId} }\n`;
          }
        });
        const defaultConn = ruleConns.find(c => c.fromPortIdx === 0);
        if (defaultConn) {
          csml += `  goto node_${defaultConn.toId}\n`;
        }
      } else {
        const apiConns = connectionsFrom(connections, node.id);
        if (apiConns.length > 0) {
          csml += `  goto node_${apiConns[0].toId}\n`;
        }
      }
    } else if (node.type === 'assign') {
      // Assign is a native component the engine registers from its
      // COMPONENTS_DIR (csml_server/components/Assign.json), which declares
      // `type` and a numeric `id`. ProcessorService#process_assign_action
      // reads content['id'] too. team_id and availability never arrive, so
      // an available-agent handoff has to name the team as id and let the
      // processor pick an online member when the flow runs.
      const assignType = node.assignType === 'agent' ? 'agent' : 'team';
      // An unset picker is '' and Number('') is 0, so the emptiness check has
      // to come before the numeric one - otherwise an unconfigured node
      // silently emits id=0 and assigns the conversation to nothing.
      const rawId = assignType === 'agent' ? node.agentId : node.teamId;
      const targetId = Number(rawId);
      if (String(rawId ?? '').trim() && Number.isFinite(targetId)) {
        csml += `  say Assign(type="${assignType}", id=${targetId})\n`;
        if (assignType === 'team' && node.assignAvailableAgent) {
          csml += `  say Assign(type="available_agent", id=${targetId})\n`;
        }
      } else {
        csml += `  // Assign node - please configure a team or an agent\n`;
      }
      if (node.content) {
        csml += `  say "${escapeCSML(node.content)}"\n`;
      }
    } else if (node.type === 'oauth') {
      csml += `  say OAuthRequest(provider="${escapeCSML(node.provider || 'google')}")\n`;
    } else if (node.type === 'hold') {
      csml += `  hold\n`;
    } else if (node.type === 'question') {
      csml += appendQuestion(node, connections);
    } else if (node.type === 'quickreply') {
      (node.buttons || []).forEach((b, i) => {
        const payload = b.payload ? `, payload="${escapeCSML(b.payload)}"` : '';
        csml += `  do qr_${node.id}_${i} = Button("${escapeCSML(b.label)}"${payload})\n`;
      });
      csml += `  say QuickReply("${escapeCSML(node.content)}", buttons=[${(
        node.buttons || []
      )
        .map((_, i) => `qr_${node.id}_${i}`)
        .join(',')}])\n`;
      const qrConns = connectionsFrom(connections, node.id);
      if (qrConns.length > 0) {
        csml += `  hold\n`;
        qrConns.forEach(conn => {
          const btnIdx = conn.fromPortIdx !== undefined ? conn.fromPortIdx : 0;
          if (node.buttons?.[btnIdx]) {
            csml += `  if (event match qr_${node.id}_${btnIdx}) { goto node_${conn.toId} }\n`;
          }
        });
      }
    } else if (node.type === 'carousel') {
      (node.cards || []).forEach((card, i) => {
        (card.buttons || []).forEach((b, j) => {
          const payload = b.payload
            ? `, payload="${escapeCSML(b.payload)}"`
            : '';
          csml += `  do card${i}_btn${j} = Button("${escapeCSML(b.label)}"${payload})\n`;
        });
        csml += `  do card${i} = Card("${escapeCSML(card.title)}"`;
        if (card.subtitle) {
          csml += `, subtitle="${escapeCSML(card.subtitle)}"`;
        }
        if (card.image_url) {
          csml += `, image_url="${escapeCSML(card.image_url)}"`;
        }
        if (card.buttons && card.buttons.length > 0) {
          const btnStr = card.buttons
            .map((_, j) => `card${i}_btn${j}`)
            .join(',');
          csml += `, buttons=[${btnStr}]`;
        }
        csml += `)\n`;
      });
      const autoplay = node.autoplay ? ', autoplay=true' : '';
      csml += `  say Carousel(cards=[${(node.cards || [])
        .map((_, i) => `card${i}`)
        .join(',')}]${autoplay})\n`;
      const carConns = connectionsFrom(connections, node.id);
      if (carConns.length > 0) {
        csml += `  hold\n`;
        const carPorts = getOutPorts(node);
        carConns.forEach(conn => {
          const idx = conn.fromPortIdx !== undefined ? conn.fromPortIdx : 0;
          const port = carPorts.find(p => p.idx === idx);
          if (port && port.type === 'cardbtn') {
            csml += `  if (event match card${port.cardIndex}_btn${port.buttonIndex}) { goto node_${conn.toId} }\n`;
          } else {
            csml += `  // Card ${port ? port.cardIndex : idx} has no button to branch on\n`;
          }
        });
      }
    } else if (node.type === 'calendar') {
      let params = `input_mode="${node.mode || 'single'}"`;
      if (node.min_date) params += `, min_date="${node.min_date}"`;
      if (node.max_date) params += `, max_date="${node.max_date}"`;
      csml += `  say Calendar(${params})\n`;
      const calConns = connectionsFrom(connections, node.id);
      if (calConns.length > 0) {
        csml += `  hold\n`;
        csml += `  goto node_${calConns[0].toId}\n`;
      }
    } else if (node.type === 'input') {
      let params = `type="${node.inputType || 'text'}"`;
      if (node.title) params += `, title="${escapeCSML(node.title)}"`;
      if (node.description) {
        params += `, description="${escapeCSML(node.description)}"`;
      }
      if (node.placeholder) {
        params += `, placeholder="${escapeCSML(node.placeholder)}"`;
      }
      if (node.required) params += `, required=true`;
      if (node.minlength > 0) params += `, minlength=${node.minlength}`;
      if (node.maxlength > 0) params += `, maxlength=${node.maxlength}`;
      if (node.submit_label) {
        params += `, submit_label="${escapeCSML(node.submit_label)}"`;
      }
      csml += `  say Input(${params})\n`;
      const inConns = connectionsFrom(connections, node.id);
      if (inConns.length > 0) {
        csml += `  hold\n`;
        csml += `  goto node_${inConns[0].toId}\n`;
      }
    } else if (node.type === 'radio') {
      (node.options || []).forEach((o, i) => {
        csml += `  do opt_${node.id}_${i} = Button("${escapeCSML(o.label)}", payload="${escapeCSML(o.payload)}")\n`;
      });
      let params = `options=[${(node.options || [])
        .map((_, i) => `opt_${node.id}_${i}`)
        .join(',')}]`;
      if (node.title) params += `, title="${escapeCSML(node.title)}"`;
      if (node.description) {
        params += `, description="${escapeCSML(node.description)}"`;
      }
      if (node.selected) params += `, selected="${escapeCSML(node.selected)}"`;
      csml += `  say Radio(${params})\n`;
      const radConns = connectionsFrom(connections, node.id);
      if (radConns.length > 0) {
        csml += `  hold\n`;
        radConns.forEach(conn => {
          const idx = conn.fromPortIdx !== undefined ? conn.fromPortIdx : 0;
          csml += `  if (event match opt_${node.id}_${idx}) { goto node_${conn.toId} }\n`;
        });
      }
    } else if (node.type === 'multiselect' || node.type === 'checkbox') {
      const compName = node.type === 'multiselect' ? 'Multiselect' : 'Checkbox';
      (node.options || []).forEach((o, i) => {
        csml += `  do opt_${node.id}_${i} = Button("${escapeCSML(o.label)}", payload="${escapeCSML(o.payload)}")\n`;
      });
      let params = `options=[${(node.options || [])
        .map((_, i) => `opt_${node.id}_${i}`)
        .join(',')}]`;
      if (node.title) params += `, title="${escapeCSML(node.title)}"`;
      if (node.description) {
        params += `, description="${escapeCSML(node.description)}"`;
      }
      if (node.min > 0) params += `, min=${node.min}`;
      if (node.submit_label) {
        params += `, submit_label="${escapeCSML(node.submit_label)}"`;
      }
      csml += `  say ${compName}(${params})\n`;
      const selConns = connectionsFrom(connections, node.id);
      if (selConns.length > 0) {
        csml += `  hold\n`;
        csml += `  goto node_${selConns[0].toId}\n`;
      }
    } else if (node.type === 'dropdown') {
      (node.options || []).forEach((o, i) => {
        csml += `  do opt_${node.id}_${i} = Button("${escapeCSML(o.label)}", payload="${escapeCSML(o.payload)}")\n`;
      });
      let params = `options=[${(node.options || [])
        .map((_, i) => `opt_${node.id}_${i}`)
        .join(',')}]`;
      if (node.title) params += `, title="${escapeCSML(node.title)}"`;
      if (node.description) {
        params += `, description="${escapeCSML(node.description)}"`;
      }
      if (node.placeholder) {
        params += `, placeholder="${escapeCSML(node.placeholder)}"`;
      }
      if (node.selected) params += `, selected="${escapeCSML(node.selected)}"`;
      if (node.submit_label) {
        params += `, submit_label="${escapeCSML(node.submit_label)}"`;
      }
      csml += `  say Dropdown(${params})\n`;
      const dropConns = connectionsFrom(connections, node.id);
      if (dropConns.length > 0) {
        csml += `  hold\n`;
        dropConns.forEach(conn => {
          const idx = conn.fromPortIdx !== undefined ? conn.fromPortIdx : 0;
          csml += `  if (event match opt_${node.id}_${idx}) { goto node_${conn.toId} }\n`;
        });
      }
    } else if (node.type === 'workinghours') {
      if (node.enableWorkingHours) {
        const days = (
          node.workingDays || [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
          ]
        ).join(',');
        const msg = escapeCSML(
          node.outsideMessage ||
            'We are currently closed. Our working hours are {start} - {end} ({timezone}). Please try again later.'
        );
        const startParts = (node.workingStart || '09:00').split(':');
        const endParts = (node.workingEnd || '17:00').split(':');
        const startHour =
          parseInt(startParts[0], 10) + parseInt(startParts[1] || 0, 10) / 60;
        const endHour =
          parseInt(endParts[0], 10) + parseInt(endParts[1] || 0, 10) / 60;
        csml += `  // Working Hours Check\n`;
        csml += `  do now = Time.now("${node.timezone || 'UTC'}")\n`;
        csml += `  do day = now.day_of_week()\n`;
        csml += `  do hour = now.hour + now.minute/60\n`;
        csml += `  do startHour = ${startHour}\n`;
        csml += `  do endHour = ${endHour}\n`;
        csml += `  do isWorkingDay = [${days
          .split(',')
          .map(d => `"${d.trim()}"`)
          .join(',')}].includes(day)\n`;
        // The engine's expression parser only accepts `&&`; the `and`
        // keyword is a syntax error that fails the whole flow's validation.
        csml += `  if (isWorkingDay && hour >= startHour && hour < endHour) {\n`;
        const openTarget = connectionsFrom(connections, node.id)
          .filter(c => c.fromPortIdx === 0)
          .map(c => c.toId)[0];
        if (openTarget) csml += `    goto node_${openTarget}\n`;
        else csml += `    // No target connected to Open port\n`;
        csml += `  } else {\n`;
        const closedTarget = connectionsFrom(connections, node.id)
          .filter(c => c.fromPortIdx === 1)
          .map(c => c.toId)[0];
        const spoken = msg
          .replace(/\{start\}/g, node.workingStart || '09:00')
          .replace(/\{end\}/g, node.workingEnd || '17:00')
          .replace(/\{timezone\}/g, node.timezone || 'UTC');
        csml += `    say "${spoken}"\n`;
        if (closedTarget) csml += `    goto node_${closedTarget}\n`;
        else csml += `    // No target connected to Closed port\n`;
        csml += `  }\n`;
      } else {
        csml += `  // Working Hours node is disabled\n`;
      }
    }

    const next = connectionsFrom(connections, node.id);
    const skipLinearGoto = [
      'question',
      'quickreply',
      'radio',
      'multiselect',
      'checkbox',
      'dropdown',
      'calendar',
      'input',
      'signature',
      'oauth',
      'hold',
      'carousel',
      'workinghours',
      'api',
      'assign',
      'router',
    ];
    if (next.length > 0 && !skipLinearGoto.includes(node.type)) {
      csml += `  goto node_${next[0].toId}\n`;
    }
    if (['signature', 'oauth'].includes(node.type)) {
      const sigConns = connectionsFrom(connections, node.id);
      if (sigConns.length > 0) {
        csml += `  hold\n`;
        csml += `  goto node_${sigConns[0].toId}\n`;
      }
    }
    if (node.type === 'hold') {
      const holdConns = connectionsFrom(connections, node.id);
      if (holdConns.length > 0) {
        csml += `  goto node_${holdConns[0].toId}\n`;
      }
    }
    if (node.type === 'assign') {
      const assignConns = connectionsFrom(connections, node.id);
      if (assignConns.length > 0) {
        csml += `  goto node_${assignConns[0].toId}\n`;
      }
    }
    csml += '\n';
  });

  return csml;
}

export function useCsmlGenerator() {
  return { escapeCSML, generateCSML };
}
